import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTenant, useAuth } from '@/auth';
import { captureEvent } from '@/posthog-analytics/posthog';
import { getPersonaForRoute, INKEEP_CONFIG } from './config';
import { MitraGhostIcon } from './MitraGhostIcon';
import {
  ApiContext,
  Message,
  FileAttachment,
  ConversationSummary,
  callInkeepSSE,
  fetchInkeepToken,
  pollMessages,
  postMessage,
  fetchConversations,
  pollMessageToLocal,
  resolveConversation,
  keepTicketOpen,
} from './api';
import { loadChatState, saveChatState, clearChatState } from './storage';

type ViewState = 'pill' | 'panel' | 'fullscreen';
type ConversationStatus = 'active' | 'escalated' | 'resolved';

/* ------------------------------------------------------------------ */
/*  Welcome-card icon paths (Heroicons outline 24×24)                 */
/* ------------------------------------------------------------------ */
const svgIcon = (paths: string[]) => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    {paths.map((d, i) => <path key={i} strokeLinecap="round" strokeLinejoin="round" d={d} />)}
  </svg>
);

type WelcomeCard = { icon: React.ReactNode; title: string; desc: string };

const RECRUITER_CARDS: WelcomeCard[] = [
  { icon: svgIcon(['M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0']), title: 'Job & candidates', desc: 'Look up jobs, applications, or candidate details.' },
  { icon: svgIcon(['M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6']), title: 'Interview summary', desc: 'Get a summary of a candidate\'s interview.' },
  { icon: svgIcon(['M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z', 'M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z']), title: 'Interview analytics', desc: 'View pass rates, completion stats, or scoring trends.' },
  { icon: svgIcon(['M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18']), title: 'Design interview', desc: 'Help me create or tweak an interview plan.' },
];

const MEETING_CARDS: WelcomeCard[] = [
  { icon: svgIcon(['M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z']), title: 'Audio & video', desc: 'My camera or mic isn\'t working properly.' },
  { icon: svgIcon(['M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z']), title: 'Interruptions', desc: 'The interviewer is interrupting me a lot.' },
  { icon: svgIcon(['M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 20.25a.75.75 0 110-1.5.75.75 0 010 1.5z']), title: 'Disconnecting', desc: 'The interview keeps disconnecting due to technical issues.' },
  { icon: svgIcon(['M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z']), title: 'Talk to support', desc: 'Connect me with a human support agent.' },
];

const CANDIDATE_CARDS: WelcomeCard[] = [
  { icon: svgIcon(['M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9']), title: 'Join interview', desc: 'Enter or access your scheduled interview.' },
  { icon: svgIcon(['M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z']), title: 'Resume upload', desc: 'I\'m having trouble uploading or submitting my resume.' },
  { icon: svgIcon(['M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z']), title: 'Trouble joining', desc: 'My interview isn\'t loading or I can\'t get in.' },
  { icon: svgIcon(['M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z']), title: 'Talk to support', desc: 'Connect me with a human support agent.' },
];

function getWelcomeCards(subAgentId: string | undefined, isMeetingView: boolean): WelcomeCard[] {
  if (subAgentId === 'recruiter-help') return RECRUITER_CARDS;
  if (isMeetingView) return MEETING_CARDS;
  return CANDIDATE_CARDS;
}

/* ------------------------------------------------------------------ */
/*  Icons (inline SVGs)                                               */
/* ------------------------------------------------------------------ */

const CloseIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const MaximizeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
  </svg>
);

const RestoreIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H4v4M16 4h4v4M8 20H4v-4M16 20h4v-4" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M3.478 2.405a.75.75 0 0 0-.926.94l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.405Z" />
  </svg>
);

const SidebarToggleIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {collapsed ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
    )}
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const PaperclipIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
  </svg>
);

const ACCEPTED_FILE_TYPES = '.csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function fileTypeLabel(mimeType: string): string {
  if (mimeType === 'text/csv') return 'CSV';
  if (mimeType.includes('spreadsheetml')) return 'XLSX';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.startsWith('image/')) return mimeType.split('/')[1]?.toUpperCase() || 'IMG';
  return 'FILE';
}

const MitraAvatar = ({ size = 'md', whiteFill = false }: { size?: 'sm' | 'md' | 'lg'; whiteFill?: boolean }) => {
  const dims = size === 'sm' ? 'w-7 h-7' : size === 'lg' ? 'w-14 h-14' : 'w-8 h-8';
  return (
    <div className={`${dims} flex items-center justify-center flex-shrink-0 text-primary`}>
      <MitraGhostIcon className="w-full h-full" whiteFill={whiteFill} />
    </div>
  );
};

const WELCOME_MESSAGES = [
  "Hey there! What can I help you with today?",
  "Hi! I'm here to help — ask me anything.",
  "Welcome! How can I assist you?",
  "Hey! Got a question? I'm all ears.",
  "Hi there! What's on your mind?",
  "Hello! Need a hand with something?",
  "Hey! How can I make your day easier?",
];

/* ------------------------------------------------------------------ */
/*  Read-receipt ticks                                                 */
/* ------------------------------------------------------------------ */

const SingleTick = () => (
  <svg className="w-3.5 h-3.5 text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const DoubleTick = () => (
  <svg className="w-3.5 h-3.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M1 13l4 4L15 7" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 13l4 4L21 7" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Typing indicator                                                   */
/* ------------------------------------------------------------------ */

const TypingDots = () => (
  <div className="flex items-center gap-1 px-1 py-1">
    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
  </div>
);

/* ------------------------------------------------------------------ */
/*  Date formatting                                                    */
/* ------------------------------------------------------------------ */

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMessageTime(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (d.toDateString() === now.toDateString()) return time;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

interface SupportChatProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchored?: boolean;
}

export function SupportChat({ open, onOpenChange, anchored }: SupportChatProps = {}) {
  const { pathname } = useLocation();
  const { domain: tenantDomain } = useTenant();
  const { user, isAuthenticated } = useAuth();
  const persona = getPersonaForRoute(pathname);
  const isMeetingView = /\/interview\/[^/]+\/assessment/.test(pathname);
  const themeClass = isMeetingView ? 'theme-support-chat-meeting' : 'theme-support-chat';

  const userId = user ? String(user.id) : '';

  const [inkeepToken, setInkeepToken] = useState<string | undefined>(undefined);
  const tokenTimerRef = useRef<number | null>(null);
  const welcomeMessageRef = useRef(WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)]);
  const welcomeMessage = welcomeMessageRef.current;

  // Called by api.ts fetch helpers on 401. Fetches a fresh JWT, stores it for
  // future calls, and returns it so the failing request can retry once.
  const refreshInkeepToken = useCallback(async (): Promise<string> => {
    const fresh = await fetchInkeepToken();
    setInkeepToken(fresh);
    return fresh;
  }, []);

  const apiCtx: ApiContext = {
    agentId: persona?.agentId ?? '',
    subAgentId: persona?.subAgentId ?? '',
    tenantId: 'default',
    userEmail: user?.email,
    token: inkeepToken,
    onAuthFailure: refreshInkeepToken,
  };

  const [view, setView] = useState<ViewState>(open ? 'panel' : 'pill');

  // Optional external control of the launcher. When `open` is passed, the
  // parent owns visibility (e.g. the coding-assessment header's Support
  // button) and the built-in bottom-right pill is suppressed (see the
  // `controlled` guard in the pill render below). `open` drives the panel
  // open; an internal minimize reports back via `onOpenChange(false)`.
  const controlled = open !== undefined;
  useEffect(() => {
    if (!controlled) return;
    if (open && view === 'pill') setView('panel');
    else if (!open && view !== 'pill') setView('pill');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, controlled]);
  useEffect(() => {
    if (controlled && view === 'pill') onOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, controlled]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileAttachment[]>([]);
  const [convStatus, setConvStatus] = useState<ConversationStatus>('active');
  const ticketOpen = convStatus === 'escalated';
  const ticketClosed = convStatus === 'resolved';
  const [ticketNumber, setTicketNumber] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastReadBySupportAt, setLastReadBySupportAt] = useState<string | null>(null);

  // Sidebar / history
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const convIdRef = useRef<string | null>(null);
  const pollingRef = useRef<number | null>(null);
  const knownMsgIdsRef = useRef<Set<string>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);
  const resumedForRef = useRef<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep apiCtx in a ref for async callbacks
  const apiCtxRef = useRef(apiCtx);
  apiCtxRef.current = apiCtx;

  // Keep convStatus in a ref so persist() always reads the latest value
  // even when called from async contexts where the state closure is stale
  const convStatusRef = useRef<ConversationStatus>('active');
  convStatusRef.current = convStatus;

  /* Auto-scroll */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* Cleanup polling + token refresh on unmount */
  useEffect(() => () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (tokenTimerRef.current) clearInterval(tokenTimerRef.current);
  }, []);

  /* Fetch JWT token for Inkeep auth */
  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => {
      fetchInkeepToken()
        .then(t => { setInkeepToken(t); })
        .catch(() => { /* will retry on next interval */ });
    };
    refresh();
    tokenTimerRef.current = window.setInterval(refresh, 25 * 60 * 1000); // refresh every 25 min
    return () => { if (tokenTimerRef.current) clearInterval(tokenTimerRef.current); };
  }, [isAuthenticated]);

  /* Auto-resize textarea */
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 96)}px`; // max ~4 lines
  }, [input]);

  /* In fullscreen, always show sidebar on desktop; close it in panel */
  useEffect(() => {
    if (view === 'fullscreen') {
      setSidebarOpen(true);
    } else if (view === 'panel') {
      setSidebarOpen(false);
    }
  }, [view]);

  /* ---- Persistence helpers ---- */

  const persist = useCallback(() => {
    if (!userId || !persona) return;
    saveChatState(tenantDomain, userId, persona.agentId, {
      conversationId: convIdRef.current,
      convStatus: convStatusRef.current,
    });
  }, [tenantDomain, userId, persona]);

  /* ---- Polling ---- */

  // Single poll tick — extracted so the interval AND visibility-change handler
  // can both fire it. Returns nothing; logs failure class but doesn't throw.
  const pollOnce = useCallback(async () => {
    const convId = convIdRef.current;
    if (!convId) return;
    try {
      const result = await pollMessages(INKEEP_CONFIG.baseUrl, apiCtxRef.current, convId);
      const newCsmReplies: Message[] = [];
      for (const msg of result.messages) {
        if (knownMsgIdsRef.current.has(msg.id)) continue;
        if (msg.role === 'csm') {
          newCsmReplies.push({
            id: msg.id,
            role: 'csm',
            content: msg.content,
            createdAt: msg.createdAt,
            authorName: msg.authorName,
            actions: msg.actions,
          });
          knownMsgIdsRef.current.add(msg.id);
        }
      }
      if (newCsmReplies.length > 0) {
        setMessages(prev => [...prev, ...newCsmReplies]);
      }
      if (result.lastReadBySupportAt) {
        setLastReadBySupportAt(result.lastReadBySupportAt);
      }
      if (result.ticketNumber) {
        setTicketNumber(result.ticketNumber);
      }

      // CSM force-close (e.g. Resolve button on Slack) — auto-close without prompting
      if (result.status === 'resolved') {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        convStatusRef.current = 'resolved';
        setConvStatus('resolved');
        persist();
        setMessages(prev => [
          ...prev,
          { id: `sys-resolve-${Date.now()}`, role: 'system', content: 'Your support ticket has been resolved.' },
        ]);
      }
    } catch (err) {
      // Classify for debuggability — rendered console.warn beats silent {}.
      // The token refresh + retry already happened inside pollMessages via
      // fetchWithAuthRetry, so a surfaced 'auth' here means the refresh itself
      // also failed. Non-auth classes (timeout/network/server) are expected
      // transients — will retry on next interval.
      const { classifyFetchFailure } = await import('./fetch-with-retry');
      console.warn('[support-chat] poll failed', { class: classifyFetchFailure(err), err });
    }
  }, [persist]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = window.setInterval(pollOnce, 5000);
  }, [pollOnce]);

  // Tab returning to foreground: setInterval was throttled to ~1/min, catch
  // up immediately instead of waiting for the next lazy tick. Also recovers
  // from "machine went to sleep" scenarios where the timer effectively paused.
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      if (!pollingRef.current || !convIdRef.current) return;
      // Fire-and-forget: pollOnce catches its own errors.
      void pollOnce();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [pollOnce]);

  /* ---- Resume on mount + preload history ---- */

  useEffect(() => {
    if (!persona) return;
    const resumeKey = `${tenantDomain}:${userId}:${persona.agentId}`;
    // Wait for the Inkeep JWT before running — fetchConversations/pollMessages
    // require Bearer auth. Re-runs when inkeepToken flips from undefined → string.
    if (!isAuthenticated || !userId || !inkeepToken || resumedForRef.current === resumeKey) return;
    resumedForRef.current = resumeKey;

    // Preload conversation history so sidebar is ready
    loadHistory();

    const stored = loadChatState(tenantDomain, userId, persona.agentId);
    if (!stored?.conversationId) return;

    const convId = stored.conversationId;
    convIdRef.current = convId;

    // Immediately restore convStatus from storage to avoid flash on refresh.
    // The poll below will confirm or correct this within ~200ms.
    if (stored.convStatus && stored.convStatus !== 'active') {
      convStatusRef.current = stored.convStatus;
      setConvStatus(stored.convStatus);
    }

    (async () => {
      try {
        const result = await pollMessages(INKEEP_CONFIG.baseUrl, apiCtxRef.current, convId);
        const localMsgs = result.messages.map(pollMessageToLocal);
        setMessages(localMsgs);
        for (const m of result.messages) knownMsgIdsRef.current.add(m.id);
        if (result.lastReadBySupportAt) {
          setLastReadBySupportAt(result.lastReadBySupportAt);
        }
        if (result.ticketNumber) {
          setTicketNumber(result.ticketNumber);
        }
        // Poll is authoritative — correct local state if it diverged
        if (result.status === 'escalated') {
          convStatusRef.current = 'escalated';
          setConvStatus('escalated');
          startPolling();
        } else if (result.status === 'resolved') {
          convStatusRef.current = 'resolved';
          setConvStatus('resolved');
        } else {
          convStatusRef.current = 'active';
          setConvStatus('active');
        }
      } catch {
        // Conversation no longer exists — start fresh
        clearChatState(tenantDomain, userId, persona.agentId);
        convIdRef.current = null;
      }
    })();
  }, [isAuthenticated, userId, tenantDomain, persona, startPolling, inkeepToken]);

  /* ---- Core actions ---- */

  function handleMessageAction(msgId: string, action: string) {
    // Remove the action buttons from the prompt message so they can't be clicked again
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, actions: undefined } : m));

    if (action === 'close-ticket') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      convStatusRef.current = 'resolved';
      setConvStatus('resolved');
      setMessages(prev => [
        ...prev,
        { id: `sys-${Date.now()}`, role: 'system', content: 'Your support ticket has been closed. Feel free to reach out anytime!' },
      ]);
      // Notify Inkeep backend so DB + Slack are updated
      if (convIdRef.current) {
        resolveConversation(INKEEP_CONFIG.baseUrl, apiCtxRef.current, convIdRef.current).catch(() => {
          // Resolve failed — ticket is closed locally, backend will eventually sync
        });
      }
      persist();
    } else if (action === 'keep-ticket-open') {
      // Restart polling since the user still needs help
      startPolling();
      setMessages(prev => [
        ...prev,
        {
          id: `assist-${Date.now()}`,
          role: 'assistant',
          content: "No worries — your ticket stays open. A support agent will continue to assist you.",
          createdAt: new Date().toISOString(),
        },
      ]);
      // Notify backend so Slack thread gets action buttons re-posted
      if (convIdRef.current) {
        keepTicketOpen(INKEEP_CONFIG.baseUrl, apiCtxRef.current, convIdRef.current).catch(() => {});
      }
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;
    if (!fileList) return;
    for (const file of Array.from(fileList)) {
      if (file.size > MAX_FILE_SIZE) {
        setMessages(p => [...p, { id: `err-${Date.now()}`, role: 'system', content: `${file.name} exceeds the 10 MB limit.` }]);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUri = reader.result as string;
        setPendingFiles(prev => [...prev, { name: file.name, mimeType: file.type, dataUri }]);
      };
      reader.readAsDataURL(file);
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  }

  function removePendingFile(index: number) {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }

  const activeSendRef = useRef<string | null>(null);

  async function send(text: string, files?: FileAttachment[]) {
    const id = `a-${Date.now()}`;
    activeSendRef.current = id;
    setMessages(p => [...p, { id, role: 'assistant', content: '', createdAt: new Date().toISOString() }]);

    try {
      const result = await callInkeepSSE(
        INKEEP_CONFIG.baseUrl,
        apiCtxRef.current,
        convIdRef.current,
        text,
        (streamed) => setMessages(p => p.map(m => m.id === id ? { ...m, content: streamed } : m)),
        () => {
          // Only re-enable input if this is still the active send (not superseded)
          if (activeSendRef.current === id) setStreaming(false);
        },
        files,
      );

      // Only process post-stream results if this send wasn't superseded
      if (activeSendRef.current !== id) return;

      convIdRef.current = result.convId;

      // Eager poll after every turn — poll response is the authoritative source
      // for conversation state. If status is 'escalated', enter ticket mode and
      // start the 5s polling interval. This also seeds knownMsgIdsRef so we
      // don't show duplicates when the interval fires for the first time.
      try {
        const current = await pollMessages(INKEEP_CONFIG.baseUrl, apiCtxRef.current, result.convId);
        for (const msg of current.messages) knownMsgIdsRef.current.add(msg.id);
        if (current.ticketNumber) setTicketNumber(current.ticketNumber);
        if (current.status === 'escalated' && convStatusRef.current !== 'escalated') {
          convStatusRef.current = 'escalated';
          setConvStatus('escalated');
          startPolling();
        }
      } catch {
        // Eager poll failed. The server may have flipped the conversation to
        // 'escalated' (e.g. the LLM ran escalate-to-slack successfully but our
        // poll request timed out / 401'd). Without polling running, the client
        // would stay in 'active' mode and route future user turns through the
        // agent instead of CSM. Start polling defensively — it fetches state
        // on the first tick and enters ticket mode if the server agrees.
        if (!pollingRef.current) startPolling();
      }

      persist();
    } catch (err) {
      // Remove the empty placeholder assistant message on failure
      setMessages(p => p.filter(m => m.id !== id));
      throw err;
    }
  }

  async function sendTicketMessage(text: string, files?: FileAttachment[]) {
    const convId = convIdRef.current;
    if (!convId) return;
    await postMessage(INKEEP_CONFIG.baseUrl, apiCtxRef.current, convId, text, files);
  }

  async function handleSend() {
    const t = input.trim();
    if ((!t && pendingFiles.length === 0) || streaming) return;
    const filesToSend = pendingFiles.length > 0 ? [...pendingFiles] : undefined;
    setInput('');
    setPendingFiles([]);
    setMessages(p => [...p, {
      id: `u-${Date.now()}`,
      role: 'user',
      content: t || (filesToSend ? `Uploaded ${filesToSend.map(f => f.name).join(', ')}` : ''),
      createdAt: new Date().toISOString(),
      files: filesToSend,
    }]);
    setStreaming(true);
    try {
      if (ticketOpen) {
        await sendTicketMessage(t, filesToSend);
      } else {
        await send(t || 'I uploaded a file.', filesToSend);
      }
    } catch {
      setMessages(p => [...p, { id: `err-${Date.now()}`, role: 'system', content: "Hmm, that didn't go through. Could you try sending again?" }]);
    } finally {
      setStreaming(false);
    }
  }

  function handleNewChat() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    convIdRef.current = null;
    knownMsgIdsRef.current.clear();
    setMessages([]);
    convStatusRef.current = 'active';
    setConvStatus('active');
    setTicketNumber(null);
    if (userId && persona) clearChatState(tenantDomain, userId, persona.agentId);
    setSidebarOpen(false);
  }

  async function loadConversation(convId: string) {
    convIdRef.current = convId;
    knownMsgIdsRef.current.clear();
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    convStatusRef.current = 'active';
    setConvStatus('active');
    setStreaming(true);
    // In panel mode, close sidebar after selecting
    if (view === 'panel') setSidebarOpen(false);
    try {
      const result = await pollMessages(INKEEP_CONFIG.baseUrl, apiCtxRef.current, convId);
      const localMsgs = result.messages.map(pollMessageToLocal);
      setMessages(localMsgs);
      for (const m of result.messages) knownMsgIdsRef.current.add(m.id);
      setTicketNumber(result.ticketNumber ?? null);
      // Restore status from server — poll is authoritative
      if (result.status === 'escalated') {
        convStatusRef.current = 'escalated';
        setConvStatus('escalated');
        startPolling();
      } else if (result.status === 'resolved') {
        convStatusRef.current = 'resolved';
        setConvStatus('resolved');
      }
      persist();
    } catch {
      setMessages([{ id: `err-${Date.now()}`, role: 'system', content: "Couldn't load this conversation. It may have expired." }]);
    } finally {
      setStreaming(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const convs = await fetchConversations(INKEEP_CONFIG.baseUrl, apiCtxRef.current);
      setConversations(convs);
    } catch {
      setConversations([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  /* ---- Route guard ---- */
  if (!persona) return null;

  /* ---- Auth guard ---- */
  if (!isAuthenticated) return null;

  /* ---- Pill (minimized) ---- */
  if (view === 'pill') {
    // Externally controlled (header launcher): no built-in pill — render nothing.
    if (controlled) return null;
    return (
      <button
        onClick={() => {
          const jobMatch = pathname.match(/\/job\/(\d+)/);
          const interviewMatch = pathname.match(/\/interview\/([^/]+)/);
          captureEvent('support_chat_opened', { page_name: pathname, job_id: jobMatch?.[1], interview_id: interviewMatch?.[1] });
          setView('panel');
        }}
        className={`fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-[24px] rounded-br-none bg-gradient-to-br from-primary-400 to-primary-900 text-primary-foreground shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:from-primary-700 hover:to-primary-900 transition-all duration-200 ${themeClass}`}
      >
        {ticketOpen && (
          <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
          </span>
        )}
        <MitraGhostIcon className="w-10 h-10" animate fast inverted />
      </button>
    );
  }

  /* ================================================================ */
  /*  LAYOUT                                                           */
  /* ================================================================ */

  const isFullscreen = view === 'fullscreen';
  const sidebarWidth = isFullscreen ? 'w-64' : 'w-full';

  const containerClass = isFullscreen
    ? 'fixed inset-4 z-[1050] flex flex-col rounded-2xl shadow-2xl border border-border bg-background overflow-hidden'
    // `anchored`: the panel is rendered inside a Popover anchored to a trigger
    // (the coding-assessment header's Support button), so it lays out in
    // normal flow (relative) instead of pinning itself bottom-right.
    : anchored
      ? 'relative flex flex-col w-[28rem] max-w-[calc(100vw-3rem)] h-[36rem] max-h-[calc(100vh-6rem)] rounded-2xl shadow-2xl border border-border bg-background overflow-hidden'
      : 'fixed bottom-6 right-6 z-50 flex flex-col w-[28rem] max-w-[calc(100vw-3rem)] h-[36rem] max-h-[calc(100vh-3rem)] rounded-2xl shadow-2xl border border-border bg-background overflow-hidden';

  function renderMessageBubble(msg: Message) {
    if (msg.role === 'system') {
      return (
        <div key={msg.id} className="flex justify-center">
          <div className="bg-destructive/10 text-destructive text-xs rounded-lg px-3 py-1.5 max-w-[85%]">
            {msg.content}
          </div>
        </div>
      );
    }
    if (msg.role === 'user') {
      return (
        <div key={msg.id} className="flex flex-col items-end gap-0.5">
          <span className="text-[10px] font-semibold text-muted-foreground">You</span>
          {msg.files && msg.files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-end max-w-[80%] overflow-hidden">
              {msg.files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-primary-800/60 text-white text-xs rounded-lg px-2.5 py-1">
                  <PaperclipIcon />
                  <span className="truncate max-w-[120px]">{f.name}</span>
                  <span className="text-primary-300 text-[10px]">{fileTypeLabel(f.mimeType)}</span>
                </span>
              ))}
            </div>
          )}
          <div className="max-w-[80%] bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-3.5 py-2 text-sm whitespace-pre-wrap support-chat-bubble">
            {msg.content}
          </div>
          <div className="flex items-center gap-1">
            {msg.createdAt && <span className="text-[9px] text-muted-foreground/60">{formatMessageTime(msg.createdAt)}</span>}
            {msg.content && (
              lastReadBySupportAt && msg.createdAt && msg.createdAt < lastReadBySupportAt
                ? <DoubleTick />
                : <SingleTick />
            )}
          </div>
        </div>
      );
    }
    const isCsm = msg.role === 'csm';
    const senderName = isCsm ? (msg.authorName || 'Support Team') : 'Mitra';
    const avatarInitial = isCsm && msg.authorName ? msg.authorName.charAt(0).toUpperCase() : null;
    return (
      <div key={msg.id} className="flex justify-start gap-2">
        {isCsm ? (
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-green-100">
            <span className="text-green-700 text-xs font-bold">{avatarInitial || 'S'}</span>
          </div>
        ) : (
          <div className="flex-shrink-0 mt-0.5"><MitraAvatar size="sm" whiteFill={isMeetingView} /></div>
        )}
        <div className="flex flex-col gap-0.5 max-w-[80%] min-w-0">
          <span className={`text-[10px] font-semibold ${isCsm ? 'text-green-600' : 'text-muted-foreground'}`}>{senderName}</span>
          <div className={`rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm ${
            isCsm ? 'bg-green-50 border border-green-200 text-green-900' : 'bg-muted text-foreground'
          } support-chat-markdown`}>
            {msg.content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown> : <TypingDots />}
          </div>
          {msg.actions && msg.actions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1.5 overflow-hidden">
              {msg.actions.map((act) => (
                <button
                  key={act.action}
                  onClick={() => handleMessageAction(msg.id, act.action)}
                  className={`text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors ${
                    act.variant === 'primary'
                      ? 'bg-primary text-primary-foreground hover:bg-primary-700'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 border border-border'
                  }`}
                >
                  {act.label}
                </button>
              ))}
            </div>
          )}
          {msg.createdAt && <span className="text-[9px] text-muted-foreground/60">{formatMessageTime(msg.createdAt)}</span>}
        </div>
      </div>
    );
  }

  return (
    <>
      {isFullscreen && (
        <div className="fixed inset-0 z-[1049] bg-black/20 backdrop-blur-sm" onClick={() => setView('panel')} />
      )}

      <div className={`${containerClass} ${themeClass} font-poppins transition-all duration-300`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-background shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={() => {
                setSidebarOpen(!sidebarOpen);
                if (!sidebarOpen && conversations.length === 0 && inkeepToken) loadHistory();
              }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shrink-0"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              <SidebarToggleIcon collapsed={!sidebarOpen} />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative shrink-0">
                <MitraAvatar size="sm" whiteFill={isMeetingView} />
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-background" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-sm text-foreground leading-tight truncate">Mitra Agent</span>
                <span className="text-[10px] leading-tight text-muted-foreground truncate">with <span className="mitra-rainbow-shimmer">human supervisor</span></span>
              </div>
              {ticketOpen && ticketNumber && (
                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium shrink-0">
                  Ticket #{ticketNumber}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* Fullscreen is hidden in the anchored header popover — it lays
                out inside a fixed-size Popover, so maximizing makes no sense
                there. Still available in the standalone (bottom-right) usage. */}
            {isFullscreen ? (
              <button onClick={() => setView('panel')} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" title="Restore">
                <RestoreIcon />
              </button>
            ) : !anchored ? (
              <button onClick={() => setView('fullscreen')} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" title="Maximize">
                <MaximizeIcon />
              </button>
            ) : null}
            <button onClick={() => setView('pill')} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors" title="Minimize">
              <ChevronDownIcon />
            </button>
          </div>
        </div>

        {/* Body: sidebar + chat */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Conversation sidebar */}
          {sidebarOpen && (
            <div className={`${isFullscreen ? `${sidebarWidth} flex-shrink-0 border-r` : 'absolute inset-0 z-10'} flex flex-col border-border bg-background h-full min-w-0 overflow-hidden`}>
              <div className="p-3">
                <button onClick={handleNewChat} className="w-full flex items-center justify-center gap-2 text-sm text-primary border border-border rounded-lg px-3 py-2 hover:bg-primary-accent-50 transition-colors">
                  <PlusIcon /> New Chat
                </button>
              </div>
              <div className="flex-1 overflow-y-auto mitra-scrollbar">
                {historyLoading ? (
                  <div className="flex justify-center py-8"><TypingDots /></div>
                ) : conversations.length === 0 ? (
                  <div className="text-center text-muted-foreground text-xs py-8 px-3">No previous conversations</div>
                ) : (
                  <div className="px-2 space-y-0.5">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          convIdRef.current === conv.id ? 'bg-muted font-medium' : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="text-xs text-foreground truncate overflow-hidden">{conv.title || 'Untitled conversation'}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate overflow-hidden">
                          {formatRelativeDate(conv.updatedAt || conv.createdAt)}
                          <span className="ml-1.5 text-muted-foreground/50">#{conv.id.slice(-6)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-2 border-t border-border">
                <button onClick={() => setSidebarOpen(false)} className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground py-1.5 rounded-lg hover:bg-muted/50 transition-colors">
                  <SidebarToggleIcon collapsed={false} /> <span>Collapse</span>
                </button>
              </div>
            </div>
          )}

          {/* Chat column */}
          <div className="flex flex-col flex-1 min-w-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 mitra-scrollbar">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center px-2 gap-5 overflow-hidden">
                  <div className="flex flex-col items-center gap-1 text-primary">
                    <MitraGhostIcon className={isFullscreen ? 'w-24 h-24' : 'w-16 h-16'} animate whiteFill={isMeetingView} />
                    <span className={`font-semibold text-foreground ${isFullscreen ? 'text-base' : 'text-sm'}`}>Hi, I'm your Mitra!</span>
                    <span className={`text-muted-foreground ${isFullscreen ? 'text-sm' : 'text-xs'}`}>{welcomeMessage}</span>
                  </div>
                  <div className={`grid grid-cols-2 w-full ${isFullscreen ? 'gap-4 max-w-xl' : 'gap-2 max-w-sm'}`}>
                    {getWelcomeCards(persona.subAgentId, isMeetingView).map((card) => (
                      <button
                        key={card.title}
                        onClick={() => { setInput(card.desc); }}
                        className={`flex flex-col gap-1 rounded-xl border border-border bg-muted/30 text-left hover:bg-muted/60 transition-colors overflow-hidden min-w-0 ${isFullscreen ? 'px-4 py-4' : 'px-3 py-2.5'}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground">{card.icon}</span>
                          <span className={`font-medium text-foreground ${isFullscreen ? 'text-sm' : 'text-xs'}`}>{card.title}</span>
                        </div>
                        <span className={`text-muted-foreground leading-snug line-clamp-2 ${isFullscreen ? 'text-xs' : 'text-[11px]'}`}>{card.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map(renderMessageBubble)}
              <div ref={bottomRef} />
            </div>

            {/* Input area */}
            <div className="shrink-0 border-t border-border bg-background px-4 py-3 space-y-2">
              {ticketClosed ? (
                <button
                  onClick={handleNewChat}
                  className="w-full flex items-center justify-center gap-2 text-sm font-medium rounded-xl bg-primary text-primary-foreground py-2.5 hover:bg-primary-700 transition-colors"
                >
                  <PlusIcon /> Start New Chat
                </button>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="space-y-2">
                  {pendingFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 overflow-hidden max-w-full">
                      {pendingFiles.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-muted text-foreground text-xs rounded-lg px-2.5 py-1 border border-border">
                          <PaperclipIcon />
                          <span className="truncate max-w-[120px]">{f.name}</span>
                          <span className="text-muted-foreground text-[10px]">{fileTypeLabel(f.mimeType)}</span>
                          <button type="button" onClick={() => removePendingFile(i)} className="ml-0.5 text-muted-foreground hover:text-foreground">
                            <CloseIcon />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      onChange={handleFileSelect}
                      className="hidden"
                      multiple
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={streaming}
                      className="rounded-xl p-2.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 disabled:opacity-50 transition-colors shrink-0"
                      title="Attach file"
                    >
                      <PaperclipIcon />
                    </button>
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                      }}
                      placeholder={streaming ? 'Mitra is typing...' : ticketOpen ? 'Reply to support team...' : 'Ask me anything...'}
                      disabled={streaming}
                      rows={1}
                      className="flex-1 bg-background border border-input rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:bg-muted resize-none mitra-scrollbar"
                      style={{ maxHeight: '96px' }}
                    />
                    <button type="submit" disabled={streaming || (!input.trim() && pendingFiles.length === 0)}
                      className="rounded-xl bg-primary p-2.5 text-primary-foreground hover:bg-primary-700 disabled:bg-muted disabled:text-muted-foreground transition-colors shrink-0">
                      <SendIcon />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
